import moment from 'moment';
import {
  Dictionary,
  isInteger,
  isNil,
  omit,
  toString
} from 'lodash';
import { IntlShape } from 'react-intl';
import { ELSDropDownOption } from '@els/els-component-form-field-react';
import { ELSAssignmentEditorGradeOption } from '@els/els-component-shared-ts-react';
import React from 'react';
import { ELSWithToastService } from '@els/els-component-toast-react';
import {
  AssignmentDto,
  AssignmentGradeType,
  AssignmentTargetType,
  AssignmentType
} from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import {
  SyllabusItemDto,
  SyllabusItemExternalIdDto
} from '../../apis/sherpath-syllabus-service/sherpath-syllabus-service.dtos';
import {
  checkAssignmentHasAvailableDate,
  checkAssignmentHasDueDate,
  checkAssignmentIsAssigned,
  defaultGradingOptions
} from '../../components/assignment-editor/assignment-editor.utilities';
import {
  getLocalMomentInsFromServicesUTC,
  isValidDate
} from '../../utilities/app.utilities';
import {
  mapToIds,
} from '../../utilities/common.utilities';
import { getTitle } from '../catalog/catalog.utilities';

import {
  assignmentHasDueDate,
  getAssignmentTypeFromSyllabusItemType,
  getIsbnFromSyllabusItem,
  getSyllabusItemTypeFromAssignment,
  isAssignmentDueDateInPast,
  isScorableType,
} from '../course-plan/syllabus.utilities';
import {
  EvolveProductDto
} from '../../apis/sherpath-app-facade-service/sherpath-app-facade-service.dtos';

import {
  ActiveSyllabusItemTypeDto,
  ExternalIdTypeDto,
} from '../../apis/sherpath-syllabus-service/sherpath-syllabus-service.constants';
import { getSimulationAssignmentInfo } from '../simulation-editor/simulation.utilities';
import { getSkillAssignmentInfo } from '../skill-assignment-editor/skill-assignment.utilities';
import { getEvolveResourceAssignmentInfo } from '../evolve-resource-assignment-editor/evolve-resource-assignment.utilities';
import {
  MAX_SYLLABUS_ITEM_TITLE_CHAR_LENGTH,
} from '../course-plan/syllabus.constants';
import {
  getCurrentDateISOString,
  getDefaultDueDateString
} from '../../hocs/with-base-assignment-editor/with-base-assignment-editor.utilities';
import {
  CatalogWithExternalEntitiesDto,
  UserDto
} from '../../apis/sherpath-course-management-service/sherpath-course-management-service.dtos';
import { PrimaryTaxonomy } from '../../apis/rec-gateway/rec-gateway.models';
import {
  BulkEditorError,
  BulkEditSettingsField,
} from './bulk-edit-settings.models';
import {
  availableDateNullErrorMessage,
  dueDateNullErrorMessage,
  dueDateNullErrorMessageTwo,
  extendDatesOptions,
  dueDateBeforeNowErrorMessage,
  PassFailOnlyAssignmentType,
  ScoredOnlyAssignmentType,
  UnSupportedGradePointsAssignmentType,
  UnSupportedGradePointsAssignmentTypeNoEAQ,
  availableAndDueDateNullErrorMessage
} from './bulk-edit-settings.constants';
import { BASE_TOAST_CONFIG } from '../../constants/toast.constants';
import { ELSToastServiceType } from '../../models/els.models';
import { Messages } from '../../translations/message.models';
import { LANGUAGE_KEY } from '../../translations/message.constants';
import { getSherpathGroupActivityAssignmentInfo } from '../sherpath-group-activity-editor/sherpath-group-activity.utilities';
import { getSherpathPowerpointAssignmentInfo } from '../sherpath-powerpoint-editor/sherpath-powerpoint.utilities';
import { getSherpathCaseStudyAssignmentInfo } from '../sherpath-case-study-editor/sherpath-case-study.utilities';
import { RecContentItemDto, RecContentItemTypeDto } from '../../apis/rec-gateway/rec-gateway.dtos';
import { getInteractiveReviewAssignmentInfo } from '../interactive-review-assignment-editor/interactive-review-assignment-editor.utilities';
import { getPreOrPostSimulationQuizAssignmentInfo } from '../pre-or-post-simulation-quiz-assignment-editor/pre-or-post-simulation-quiz-assignment-editor.utilities';
import { getPatientReportAssignmentInfo } from '../patient-report-assignment-editor/patient-report-assignment-editor.utilities';
import { getReadingAssignmentInfo } from '../reading-assignment-editor/reading-assignment-editor.utilities';
import { getSimulationExerciseAssignmentInfo } from '../simulation-exercise-assignment-editor/simulation-exercise-assignment-editor.utilities';
import { FeatureFlagsGroupedDto } from '../../apis/eols-features-api/eols-features-api.dtos';
import { getBooleanFromGroupFeatureFlagWithFallbackToGlobal } from '../../utilities/featureFlag.utilities';
import { FEATURE_FLAG } from '../../apis/eols-features-api/eols-features-api.constants';
import { ASSIGNMENT_TYPES } from '../../assignment-types';
import { ExcludedTypes, GetAssignmentInfoProps } from '../../models/assignment-types-shared.models';
import { getCatalogItemFromSyllabusItem } from '../../utilities/assignment-types-shared.utilities';
import { getSlsResourceAssignmentInfo } from '../simulation-editor/sls-resource-assignment-editor.utilities';
import { assignableSimulationSubTypes } from '../../constants/assignment-types.constants';

/**
 * Maps the three assignable SLS catalog subtypes (stored under SIMULATION_*_RESOURCE in the
 * syllabus) to their corresponding AssignmentType.  All other RecContentItemTypeDto values are
 * intentionally absent so that an unknown type returns undefined rather than throwing at runtime.
 */
export const assignableSimulationSubTypeToAssignmentType: Partial<Record<RecContentItemTypeDto, AssignmentType>> = {
  [RecContentItemTypeDto.VERBAL_REPORT_TEMPLATE]: AssignmentType.SLS_VERBAL_REPORT_TEMPLATE,
  [RecContentItemTypeDto.OBSERVER_EVALUATION_RUBRIC]: AssignmentType.SLS_OBSERVER_EVALUATION_RUBRIC,
  [RecContentItemTypeDto.PERFORMANCE_CHECKLIST]: AssignmentType.SLS_PERFORMANCE_CHECKLIST,
};

export const areDatesTheSame = (dateA: string, dateB: string): boolean => {

  if (isNil(dateA) && isNil(dateB)) {
    return true;
  }

  if (isNil(dateA) && !isNil(dateB)) {
    return false;
  }

  if (isNil(dateB) && !isNil(dateA)) {
    return false;
  }

  if (dateA === dateB) {
    return true;
  }

  return getLocalMomentInsFromServicesUTC(dateA, moment.ISO_8601)
    .isSame(getLocalMomentInsFromServicesUTC(dateB, moment.ISO_8601));
};

export const areDueDatesSame = (assignmentA: AssignmentDto, assignmentB: AssignmentDto): boolean => {

  if (!assignmentA && !assignmentB) {
    return true;
  }

  const dateA = assignmentA ? assignmentA.dueDate : null;
  const dateB = assignmentB ? assignmentB.dueDate : null;

  return areDatesTheSame(dateA, dateB);

};

export const isAssignmentAllowGradePoints = (assignment: Partial<AssignmentDto>, config:{
  featureFlagsGrouped: FeatureFlagsGroupedDto[];
  courseSectionId: string;
}): boolean => {
  if (!assignment || !assignment.assignmentType) {
    return true;
  }
  const isEAQCustomGradePointsEnabled = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    config.featureFlagsGrouped,
    FEATURE_FLAG.IS_EAQ_CUSTOM_GRADE_POINTS_ENABLED,
    config.courseSectionId
  );
  if (isEAQCustomGradePointsEnabled) {
    return !Object.values(UnSupportedGradePointsAssignmentTypeNoEAQ as unknown as AssignmentDto).includes(assignment.assignmentType);
  }
  return !Object.values(UnSupportedGradePointsAssignmentType as unknown as AssignmentDto).includes(assignment.assignmentType);
};

export const validateUpdatedAssignment = (
  assignment: AssignmentDto,
  originalAssignment: AssignmentDto,
  messages: Messages,
  config: { featureFlagsGrouped: FeatureFlagsGroupedDto[]; courseSectionId: string }
  // eslint-disable-next-line sonarjs/cognitive-complexity
): BulkEditorError[] => {

  const errors = [];

  if (!assignment) {
    return errors;
  }

  const hasAvailableDate = checkAssignmentHasAvailableDate(assignment);
  const availableDateMomentIns = getLocalMomentInsFromServicesUTC(assignment.availableDate);
  if (hasAvailableDate && !isValidDate(assignment.availableDate, moment.ISO_8601)) {
    errors.push({
      message: messages.INVALID_AVAILABILITY_DATE,
      fields: [BulkEditSettingsField.AVAILABLE_FROM]
    });
  }

  const hasDueDate = checkAssignmentHasDueDate(assignment);
  if (hasDueDate && !hasAvailableDate) {
    errors.push({
      message: availableDateNullErrorMessage, // TODO: Migrate to messages
      fields: [BulkEditSettingsField.DUE]
    });
  }

  const isDueDateUpdated = !areDueDatesSame(originalAssignment, assignment);
  if (isDueDateUpdated && hasDueDate && moment.utc().isAfter(moment.utc(assignment.dueDate))) {
    errors.push({
      message: dueDateBeforeNowErrorMessage, // TODO: Migrate to messages
      fields: [BulkEditSettingsField.DUE]
    });
  }

  const shouldValidateDueDate = checkAssignmentIsAssigned(assignment);
  const dueDateMomentIns = getLocalMomentInsFromServicesUTC(assignment.dueDate);
  if (shouldValidateDueDate && !isValidDate(assignment.dueDate, moment.ISO_8601)) {
    errors.push({
      message: messages.INVALID_DUE_DATE,
      fields: [BulkEditSettingsField.DUE]
    });
  }

  if (shouldValidateDueDate
    && dueDateMomentIns.isBefore(availableDateMomentIns)) {
    errors.push({
      message: messages.DUE_DATE_BEFORE_AVAILABLE_DATE_ERROR_MESSAGE,
      fields: [BulkEditSettingsField.AVAILABLE_FROM, BulkEditSettingsField.DUE]
    });
  }

  const syllabusItemType = getSyllabusItemTypeFromAssignment(assignment, null);

  const simChartTypesRequiringDueDate = [
    ActiveSyllabusItemTypeDto.SIMCHART_CASE_STUDY,
    ActiveSyllabusItemTypeDto.SIMCHART_CHART,
  ];

  const hasGradeType = assignment && assignment.assignmentGradeType && assignment.assignmentGradeType !== AssignmentGradeType.NOT_GRADED;
  if (!hasDueDate && !simChartTypesRequiringDueDate.includes(syllabusItemType as ActiveSyllabusItemTypeDto) && hasGradeType) {
    if (isAssignmentAllowGradePoints(assignment, config)) {
      errors.push({
        message: dueDateNullErrorMessageTwo, // TODO: Migrate to messages
        fields: [BulkEditSettingsField.GRADING, BulkEditSettingsField.GRADE_POINTS]
      });
    } else {
      errors.push({
        message: dueDateNullErrorMessage, // TODO: Migrate to messages
        fields: [BulkEditSettingsField.GRADING]
      });
    }
  }

  if (hasAvailableDate && !hasDueDate && syllabusItemType === ActiveSyllabusItemTypeDto.ADAPTIVE_QUIZ) {
    errors.push({
      message: messages.DUE_DATE_IS_REQUIRED_FOR_ALL_EAQ_ASSIGNMENTS,
      fields: [BulkEditSettingsField.DUE]
    });
  }

  if (!hasDueDate && simChartTypesRequiringDueDate.includes(syllabusItemType as ActiveSyllabusItemTypeDto)) {
    if (assignment.assignmentGradeType === AssignmentGradeType.NOT_GRADED) {
      errors.push({
        message: availableAndDueDateNullErrorMessage,
        fields: [BulkEditSettingsField.AVAILABLE_FROM, BulkEditSettingsField.DUE]
      });
    } else {
      errors.push({
        message: dueDateNullErrorMessage,
        fields: [BulkEditSettingsField.GRADING]
      });
    }
  }

  if (
    assignment
    && assignment.assignmentGradeType === AssignmentGradeType.SCORED
    && Object.values(PassFailOnlyAssignmentType as unknown as AssignmentDto).includes(assignment.assignmentType)
  ) {
    errors.push({
      message: messages.ASSIGNMENT_TYPE_DOES_NOT_SUPPORT_SCORE_GRADING,
      fields: [BulkEditSettingsField.GRADING]
    });
  }

  if (
    assignment
    && assignment.assignmentGradeType === AssignmentGradeType.PASS_FAIL
    && Object.values(ScoredOnlyAssignmentType as unknown as AssignmentDto).includes(assignment.assignmentType)
  ) {
    errors.push({
      message: messages.ASSIGNMENT_TYPE_DOES_NOT_SUPPORT_PASS_FAIL_GRADING,
      fields: [BulkEditSettingsField.GRADING]
    });
  }

  return errors;
};

export const associateSyllabusItemWithAssignment = (syllabusItem: SyllabusItemDto, assignmentId: string | number): SyllabusItemDto => {
  return {
    ...syllabusItem,
    externalIdentifiers: [
      ...syllabusItem.externalIdentifiers,
      {
        type: ExternalIdTypeDto.ASSIGNMENT_ID,
        value: toString(assignmentId),
      } as SyllabusItemExternalIdDto
    ],
  };
};

export const getUpdatedSyllabusItemsState = (syllabusItems: SyllabusItemDto[], selectedSyllabusItem: SyllabusItemDto, assignmentId: string | number): SyllabusItemDto[] => {
  return syllabusItems.map(syllabusItem => {
    if (syllabusItem.id === selectedSyllabusItem.id) {
      return associateSyllabusItemWithAssignment(syllabusItem, assignmentId);
    }
    return syllabusItem;
  });
};

/**
 * Keeps only the syllabus items that still exist, and are not deleted, in the latest course
 * management response. Items missing from that response are treated as deleted too.
 */
export const getNotDeletedSyllabusItems = (
  syllabusItems: SyllabusItemDto[],
  courseManagementSyllabusItems: SyllabusItemDto[]
): SyllabusItemDto[] => {
  return syllabusItems.filter((syllabusItem) => {
    const match = courseManagementSyllabusItems.find(item => item.id === syllabusItem.id);
    return Boolean(match) && !match.isDeleted;
  });
};

export const toCoursePlanOrderMap = (syllabusItems: SyllabusItemDto[]): Record<string, number> => {
  return syllabusItems.reduce<Record<string, number>>((acc, syllabusItem, index) => {
    acc[syllabusItem.id] = index;
    return acc;
  }, {});
};

/**
 * Some assignment types can only ever be pass/fail, and some can only ever be scored, so a
 * bulk grade type change has to leave them alone.
 */
export const isGradeTypeUnsupportedForAssignment = (
  assignmentType: AssignmentType,
  updatedGrading: AssignmentGradeType
): boolean => {
  const isPassFailOnly = Object.values(PassFailOnlyAssignmentType as unknown as AssignmentType).includes(assignmentType);
  const isScoredOnly = Object.values(ScoredOnlyAssignmentType as unknown as AssignmentType).includes(assignmentType);
  return (isPassFailOnly && updatedGrading === AssignmentGradeType.SCORED)
    || (isScoredOnly && updatedGrading === AssignmentGradeType.PASS_FAIL);
};

export const getDefaultAssignment = (
  syllabusItem: SyllabusItemDto,
  courseSectionId: string,
  catalog: CatalogWithExternalEntitiesDto,
  users: UserDto[],
  primaryTaxonomies: PrimaryTaxonomy[],
  hasDueDate = false,
  hasAvailableDate = true,
): Partial<AssignmentDto> => {

  const catalogItemIsbn = getIsbnFromSyllabusItem(syllabusItem, catalog);
  const availableDate = hasAvailableDate ? getCurrentDateISOString() : null;
  const contentItem = getCatalogItemFromSyllabusItem(catalog, syllabusItem);

  if (!contentItem) {
    return null;
  }

  // For items stored as SIMULATION_STUDENT/INSTRUCTOR_RESOURCE (e.g. VRT, OER, PC),
  // getAssignmentTypeFromSyllabusItemType returns null because those syllabus types are in
  // UnassignableSyllabusItems. Fall back to the explicit narrow map for the three known
  // assignable SLS subtypes.  Using a cast to SyllabusItemTypeDto would be unsafe because
  // many RecContentItemTypeDto values (e.g. ACKNOWLEDGEMENTS, COURSE_UPDATES) are not
  // present in SyllabusItemTypeConfigMap and would throw at runtime.
  const assignmentType = getAssignmentTypeFromSyllabusItemType(syllabusItem.type)
    ?? assignableSimulationSubTypeToAssignmentType[contentItem.type] ?? null;

  return {
    id: -(Math.floor(Number(new Date()) * Math.random())),
    targetType: AssignmentTargetType.COURSE,
    assignmentGoals: [],
    assignmentType,
    availableDate,
    dueDate: hasDueDate ? getDefaultDueDateString(availableDate) : null,
    assignmentGradeType: AssignmentGradeType.NOT_GRADED,
    courseSectionId: parseInt(courseSectionId, 10),
    students: users.filter(x => x.type === 'Stu').map(mapToIds),
    isbn: catalogItemIsbn,
    studyMode: false,
    title: getTitle(contentItem, catalog, primaryTaxonomies, MAX_SYLLABUS_ITEM_TITLE_CHAR_LENGTH),
    contentId: contentItem ? contentItem.attributes.contentId : null,
    gradePoints: 100,
  };
};

type GetAssignmentInfoHandler = (props: GetAssignmentInfoProps) => Promise<AssignmentDto>

const defaultAssignmentInfoHandler = (props: GetAssignmentInfoProps): Promise<AssignmentDto> => {
  return Promise.resolve(props.newAssignment);
};

const evolveResourceAssignmentInfoHandler = (props: GetAssignmentInfoProps) => {
  const {
    newAssignment,
    catalog,
    primaryTaxonomies,
    syllabusItem,
    evolveProducts
  } = props;
  const contentItem = getCatalogItemFromSyllabusItem(catalog, syllabusItem);
  const assignmentInfo = getEvolveResourceAssignmentInfo(catalog, contentItem, primaryTaxonomies, evolveProducts);
  return Promise.resolve({
    ...newAssignment,
    ...assignmentInfo,
    title: newAssignment.title || assignmentInfo.title
  });
};

// TODO: This function can be removed once we migrate all the assignment types
const commonAssignmentInfoHandler = (
  handler: (
    catalog: CatalogWithExternalEntitiesDto,
    contentItem: RecContentItemDto,
    primaryTaxonomies: PrimaryTaxonomy[],
    evolveProducts: EvolveProductDto[]
  ) => Partial<AssignmentDto>
) => (props: GetAssignmentInfoProps) => {
  const {
    newAssignment,
    syllabusItem,
    catalog,
    primaryTaxonomies,
    evolveProducts,
    fetchEolsCollectionObjectDataAction
  } = props;
  const contentItem = getCatalogItemFromSyllabusItem(catalog, syllabusItem);
  const assignmentInfo = handler(catalog, contentItem, primaryTaxonomies, evolveProducts);

  if (syllabusItem.type !== ActiveSyllabusItemTypeDto.INTERACTIVE_REVIEW) {
    assignmentInfo.assignmentGoals[0].goal = newAssignment.gradePoints;
  }

  const isAssignableSimulationSubTypes = contentItem && assignableSimulationSubTypes.includes(contentItem.type);

  if (syllabusItem.type === ActiveSyllabusItemTypeDto.PRE_SIMULATION_QUIZ
  || syllabusItem.type === ActiveSyllabusItemTypeDto.POST_SIMULATION_QUIZ
  || syllabusItem.type === ActiveSyllabusItemTypeDto.PRE_SIMULATION_EXERCISE
  || syllabusItem.type === ActiveSyllabusItemTypeDto.POST_SIMULATION_EXERCISE
  || syllabusItem.type === ActiveSyllabusItemTypeDto.JOURNAL_EXERCISE
  || syllabusItem.type === ActiveSyllabusItemTypeDto.SBAR_EXERCISE
  || isAssignableSimulationSubTypes) {
    // EOLS Collection object data includes questions for Pre-Simulation and Post-Simulation Quiz assignments
    // and all Simulation Exercise sub-types (PRE_SIMULATION_EXERCISE, POST_SIMULATION_EXERCISE, JOURNAL_EXERCISE, SBAR_EXERCISE)
    return fetchEolsCollectionObjectDataAction(newAssignment.contentId)
      .then(questions => {
        const assignmentQuestions = questions.map((question, index) => ({
          questionVtwId: question.vtwId,
          displayOrder: index,
        }));

        const mergedAssignment = {
          ...newAssignment,
          ...assignmentInfo,
          assignmentQuestions,
          isGradePointIndependentGoals: true,
          title: newAssignment.title || assignmentInfo.title
        };
        if (mergedAssignment.assignmentGoals && mergedAssignment.assignmentGoals.length > 0) {
          mergedAssignment.assignmentGoals[0].goal = assignmentQuestions.length;
        }

        return mergedAssignment;
      });
  }

  return Promise.resolve({
    ...newAssignment,
    ...assignmentInfo,
    title: newAssignment.title || assignmentInfo.title
  });
};

// TODO: this function can be removed once we migrate all the assignment types
const AssignmentInfoHandlerMap: Record<Exclude<ActiveSyllabusItemTypeDto, ExcludedTypes>, GetAssignmentInfoHandler> = {
  [ActiveSyllabusItemTypeDto.SHERPATH_SIMULATIONS]: commonAssignmentInfoHandler(getSimulationAssignmentInfo),
  [ActiveSyllabusItemTypeDto.SHERPATH_SKILL]: commonAssignmentInfoHandler(getSkillAssignmentInfo),
  [ActiveSyllabusItemTypeDto.SHERPATH_GROUP_ACTIVITY]: commonAssignmentInfoHandler(getSherpathGroupActivityAssignmentInfo),
  [ActiveSyllabusItemTypeDto.SHERPATH_POWERPOINT]: commonAssignmentInfoHandler(getSherpathPowerpointAssignmentInfo),
  [ActiveSyllabusItemTypeDto.SHERPATH_CASE_STUDY]: commonAssignmentInfoHandler(getSherpathCaseStudyAssignmentInfo),
  [ActiveSyllabusItemTypeDto.INTERACTIVE_REVIEW]: commonAssignmentInfoHandler(getInteractiveReviewAssignmentInfo),
  [ActiveSyllabusItemTypeDto.EVOLVE_INSTRUCTOR_RESOURCE]: evolveResourceAssignmentInfoHandler,
  [ActiveSyllabusItemTypeDto.EVOLVE_STUDENT_RESOURCE]: evolveResourceAssignmentInfoHandler,
  [ActiveSyllabusItemTypeDto.FOLDER]: defaultAssignmentInfoHandler,
  [ActiveSyllabusItemTypeDto.ASSESSMENT_BUILDER_ASSIGNMENT]: defaultAssignmentInfoHandler,
  [ActiveSyllabusItemTypeDto.MCQ_QUIZ]: defaultAssignmentInfoHandler,
  [ActiveSyllabusItemTypeDto.CUSTOM_LINK]: defaultAssignmentInfoHandler,
  // VRT, OER, and PC are stored as SIMULATION_STUDENT/INSTRUCTOR_RESOURCE but are actually assignable
  [ActiveSyllabusItemTypeDto.SIMULATION_INSTRUCTOR_RESOURCE]: defaultAssignmentInfoHandler,
  [ActiveSyllabusItemTypeDto.SIMULATION_STUDENT_RESOURCE]: defaultAssignmentInfoHandler,
  [ActiveSyllabusItemTypeDto.READING_ASSIGNMENT]: commonAssignmentInfoHandler(getReadingAssignmentInfo),
  [ActiveSyllabusItemTypeDto.PATIENT_REPORT]: commonAssignmentInfoHandler(getPatientReportAssignmentInfo),
  [ActiveSyllabusItemTypeDto.LAB_SIMULATION_IMPLEMENTATION_MODULE]: null,
  [ActiveSyllabusItemTypeDto.VR_SIMULATION_IMPLEMENTATION_MODULE]: null,
  [ActiveSyllabusItemTypeDto.VR_SIMULATION]: defaultAssignmentInfoHandler,
  [ActiveSyllabusItemTypeDto.PRE_SIMULATION_QUIZ]: commonAssignmentInfoHandler(getPreOrPostSimulationQuizAssignmentInfo),
  [ActiveSyllabusItemTypeDto.POST_SIMULATION_QUIZ]: commonAssignmentInfoHandler(getPreOrPostSimulationQuizAssignmentInfo),
  [ActiveSyllabusItemTypeDto.SCMO_NG]: null,
  [ActiveSyllabusItemTypeDto.VERBAL_REPORT_TEMPLATE]: commonAssignmentInfoHandler(getSlsResourceAssignmentInfo),
  [ActiveSyllabusItemTypeDto.OBSERVER_EVALUATION_RUBRIC]: commonAssignmentInfoHandler(getSlsResourceAssignmentInfo),
  [ActiveSyllabusItemTypeDto.PERFORMANCE_CHECKLIST]: commonAssignmentInfoHandler(getSlsResourceAssignmentInfo),
  [ActiveSyllabusItemTypeDto.HESI_ASSESS]: defaultAssignmentInfoHandler,
  [ActiveSyllabusItemTypeDto.PRE_SIMULATION_EXERCISE]: commonAssignmentInfoHandler(getSimulationExerciseAssignmentInfo),
  [ActiveSyllabusItemTypeDto.POST_SIMULATION_EXERCISE]: commonAssignmentInfoHandler(getSimulationExerciseAssignmentInfo),
  [ActiveSyllabusItemTypeDto.JOURNAL_EXERCISE]: commonAssignmentInfoHandler(getSimulationExerciseAssignmentInfo),
  [ActiveSyllabusItemTypeDto.SBAR_EXERCISE]: commonAssignmentInfoHandler(getSimulationExerciseAssignmentInfo),
};

export const getAssignmentInfo = (props: GetAssignmentInfoProps): Promise<AssignmentDto> => {
  const catalogItemType = getCatalogItemFromSyllabusItem(props.catalog, props.syllabusItem)?.type as RecContentItemTypeDto;
  const syllabusItemType = assignableSimulationSubTypes.includes(catalogItemType) ? catalogItemType : props.syllabusItem.type;

  const handler = ASSIGNMENT_TYPES[syllabusItemType]?.assignmentInfoHandler ?? AssignmentInfoHandlerMap[syllabusItemType];

  return handler(props).then(assignment => {
    return omit(assignment, 'id') as AssignmentDto;
  });
};

export const getAssignmentEditorGradingOptions = (assignmentType: AssignmentType): ELSAssignmentEditorGradeOption[] => {
  if (Object.values(PassFailOnlyAssignmentType as unknown as AssignmentDto).includes(assignmentType)) {
    return defaultGradingOptions.filter(item => (item.value as string) !== AssignmentGradeType.SCORED);
  }
  if (Object.values(ScoredOnlyAssignmentType as unknown as AssignmentDto).includes(assignmentType)) {
    return defaultGradingOptions.filter(item => (item.value as string) !== AssignmentGradeType.PASS_FAIL);
  }
  return defaultGradingOptions;
};

export const getGradingOptions = (assignmentType: AssignmentType): ELSDropDownOption[] => {
  const assignmentEditorGradingOptions = getAssignmentEditorGradingOptions(assignmentType);
  return assignmentEditorGradingOptions.map(item => ({
    name: item.primaryText,
    value: item.value
  }));
};

export const getDueDateDisableBefore = () => {
  return moment().toDate();
};

export const canSyllabusItemHaveDueDate = (syllabusItem: SyllabusItemDto) => {
  return isScorableType(syllabusItem.type);
};

export const areAvailableDatesSame = (assignmentA: AssignmentDto, assignmentB: AssignmentDto): boolean => {

  if (!assignmentA && !assignmentB) {
    return true;
  }

  const dateA = assignmentA ? assignmentA.availableDate : null;
  const dateB = assignmentB ? assignmentB.availableDate : null;

  return areDatesTheSame(dateA, dateB);
};

export const isAssignmentPastDateExtended = (originalAssignment: AssignmentDto, updatedAssignment: AssignmentDto): boolean => {
  if (areDueDatesSame(originalAssignment, updatedAssignment)) {
    return false;
  }
  return originalAssignment && assignmentHasDueDate(originalAssignment) && isAssignmentDueDateInPast(originalAssignment);
};

export const isTitleSame = (syllabusItem: SyllabusItemDto, originalAssignment: AssignmentDto, assignment: AssignmentDto) => {
  if (originalAssignment) {
    return originalAssignment.title === assignment.title;
  }
  if (assignment) {
    return syllabusItem.title === assignment.title;
  }
  return true;
};

export const toastStartedAssignmentWarningGrading = (toastService: ELSToastServiceType, messages: Messages) => {
  toastService.openToast({
    ...BASE_TOAST_CONFIG,
    component: (<div>{messages.BULK_EDIT_SETTINGS_PAGE_EDIT_SETTINGS_EDIT_GRADING_STARTED_ASSIGNMENT_WARNING}</div>),
    type: ELSWithToastService.types.NEGATIVE,
  });
};

export const toastStartedAssignmentWarningGradePoints = (toastService: ELSToastServiceType, messages: Messages) => {
  toastService.openToast({
    ...BASE_TOAST_CONFIG,
    component: (<div>{messages.BULK_EDIT_SETTINGS_PAGE_EDIT_SETTINGS_EDIT_GRADE_POINTS_STARTED_ASSIGNMENT_WARNING}</div>),
    type: ELSWithToastService.types.NEGATIVE,
  });
};

export const toastNotGradedGradePointsWarning = ({
  toastService,
  messages,
  assignment,
  isShowAssignmentTitle
}: { toastService: ELSToastServiceType; messages: Messages; assignment: Partial<AssignmentDto>; isShowAssignmentTitle: boolean }) => {
  toastService.openToast({
    ...BASE_TOAST_CONFIG,
    component: (
      <>
        {isShowAssignmentTitle && <strong>{assignment.title}:</strong>}
        <div>{messages.BULK_EDIT_SETTINGS_PAGE_EDIT_SETTINGS_EDIT_NOT_GRADED_GRADE_POINTS_WARNING}</div>
      </>
    ),
    type: ELSWithToastService.types.NEGATIVE,
  });
};

export const toastIndividualUnSupportedGradePointsWarning = (toastService: ELSToastServiceType, messages: Messages) => {
  toastService.openToast({
    ...BASE_TOAST_CONFIG,
    component: (<div>{messages.BULK_EDIT_SETTINGS_PAGE_EDIT_SETTINGS_EDIT_UNSUPPORTED_ITEM_GRADE_POINTS_WARNING}</div>),
    type: ELSWithToastService.types.NEGATIVE,
  });
};

export const toastApplyToAllUnSupportedGradePointsWarning = (
  toastService: ELSToastServiceType,
  assignment: Partial<AssignmentDto>,
  intl: IntlShape,
  config: { featureFlagsGrouped: FeatureFlagsGroupedDto[]; courseSectionId: string }
) => {
  const assignmentTypeConfigMap: Record<UnSupportedGradePointsAssignmentType, string> = {
    [UnSupportedGradePointsAssignmentType.CHART]: 'SimChart Empty EHR',
    [UnSupportedGradePointsAssignmentType.CASE_STUDY]: 'Simchart Case Study',
    [UnSupportedGradePointsAssignmentType.PERSONAL_ASSESSMENT_BUILDER]: 'Assessment Builder Assignment',
    [UnSupportedGradePointsAssignmentType.STANDARD]: 'EAQ Standard',
    [UnSupportedGradePointsAssignmentType.MASTERY]: 'EAQ Mastery',
    [UnSupportedGradePointsAssignmentType.AUTHESS]: 'EAQ Authess',
    [UnSupportedGradePointsAssignmentType.QUIZ_BY_QUESTION]: 'EAQ Quiz By Question',
    [UnSupportedGradePointsAssignmentType.SLS_SIMCHART_SIMULATION_EHR]: 'Simulation EHR',
  };

  // TODO: replace original assignmentTypeConfigMap with this once IS_EAQ_CUSTOM_GRADE_POINTS_ENABLED flag is removed
  const assignmentTypeConfigMapNoEAQ: Record<UnSupportedGradePointsAssignmentTypeNoEAQ, string> = {
    [UnSupportedGradePointsAssignmentType.CHART]: 'SimChart Empty EHR',
    [UnSupportedGradePointsAssignmentType.CASE_STUDY]: 'Simchart Case Study',
    [UnSupportedGradePointsAssignmentType.PERSONAL_ASSESSMENT_BUILDER]: 'Assessment Builder Assignment',
    [UnSupportedGradePointsAssignmentType.SLS_SIMCHART_SIMULATION_EHR]: 'Simulation EHR',
  };

  const isEAQCustomGradePointsEnabled = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    config.featureFlagsGrouped,
    FEATURE_FLAG.IS_EAQ_CUSTOM_GRADE_POINTS_ENABLED,
    config.courseSectionId
  );

  toastService.openToast({
    ...BASE_TOAST_CONFIG,
    component: (
      <>
        <strong>{assignment.title}: </strong>
        {intl.formatMessage(
          {
            id: LANGUAGE_KEY.BULK_EDIT_SETTINGS_PAGE_GRADE_POINTS_APPLY_TO_ALL_UNSUPPORTED_WARNING,
            defaultMessage: '',
            description: ''
          },
          {
            assignmentType: isEAQCustomGradePointsEnabled ? assignmentTypeConfigMapNoEAQ[assignment.assignmentType] : assignmentTypeConfigMap[assignment.assignmentType],
          }
        )}
      </>
    ),
    type: ELSWithToastService.types.NEGATIVE,
  });
};

export const toastStartedAssignmentApplyAllWarning = (toastService: ELSToastServiceType, messages: Messages, field: BulkEditSettingsField) => {
  if (field === BulkEditSettingsField.GRADING) {
    toastService.openToast({
      ...BASE_TOAST_CONFIG,
      component: (<div>{messages.BULK_EDIT_SETTINGS_PAGE_EDIT_SETTINGS_TAB_CHANGE_ALL_GRADING_STARTED_ASSIGNMENT_WARNING}</div>),
      type: ELSWithToastService.types.NEGATIVE,
    });
  }

  if (field === BulkEditSettingsField.GRADE_POINTS) {
    toastService.openToast({
      ...BASE_TOAST_CONFIG,
      component: (<div>{messages.BULK_EDIT_SETTINGS_PAGE_EDIT_SETTINGS_TAB_CHANGE_ALL_GRADE_POINTS_STARTED_ASSIGNMENT_WARNING}</div>),
      type: ELSWithToastService.types.NEGATIVE,
    });
  }

};

export const toastUnpublishedLessonWarning = (toastService: ELSToastServiceType, syllabusItem: SyllabusItemDto, intl: IntlShape) => {
  toastService.openToast({
    ...BASE_TOAST_CONFIG,
    component: (
      <div>{
        intl.formatMessage({
          id: LANGUAGE_KEY.BULK_EDIT_SETTINGS_PAGE_EDIT_SETTINGS_UNPUBLISHED_LESSON_WARNING,
          defaultMessage: '',
          description: ''
        }, {
          syllabusItemName: <strong>{syllabusItem.title}:</strong>
        })
      }
      </div>
    ),
    type: ELSWithToastService.types.NEGATIVE,
  });
};

export const toastUnpublishedLessonSkippedWarning = (toastService: ELSToastServiceType, messages: Messages) => {
  toastService.openToast({
    ...BASE_TOAST_CONFIG,
    component: (
      <div>
        {messages.BULK_EDIT_SETTINGS_PAGE_EDIT_SETTINGS_UNPUBLISHED_LESSON_SKIPPED_WARNING}
      </div>
    ),
    type: ELSWithToastService.types.PRIMARY,
  });
};

export const toastNotSupportedWarning = (toastService: ELSToastServiceType) => {
  toastService.openToast({
    ...BASE_TOAST_CONFIG,
    component: (
      <div>
        This assignment type is not supported in the bulk assignment editor.
        Please return to the Course Plan and assign this syllabus item individually.
      </div>
    ),
    type: ELSWithToastService.types.NEGATIVE,
  });
};

export const toastNotSupportedItemsSkippedWarning = (toastService: ELSToastServiceType) => {
  toastService.openToast({
    ...BASE_TOAST_CONFIG,
    component: (
      <div>
        Changes not applied to SimChart assignments that have not been previously assigned.
        Please return to the Course Plan and assign each SimChart individually before updating in the bulk assignment editor.
      </div>
    ),
    type: ELSWithToastService.types.PRIMARY,
  });
};

export const toastUnassignableItemsSkippedWarning = (toastService: ELSToastServiceType) => {
  toastService.openToast({
    ...BASE_TOAST_CONFIG,
    component: (
      <div>
        These are instructor only resources.
        Changes were not applied to assignments that cannot be accessed by students.
      </div>
    ),
    type: ELSWithToastService.types.PRIMARY,
  });
};

export const toastAvailabilityDateApplySuccess = (toastService: ELSToastServiceType) => {
  toastService.openToast({
    ...BASE_TOAST_CONFIG,
    component: (<div>Availability date applied</div>),
    type: ELSWithToastService.types.POSITIVE,
  });
};

export const toastSubmissionAttemptsApplySuccess = (toastService: ELSToastServiceType, messages: Messages) => {
  toastService.openToast({
    ...BASE_TOAST_CONFIG,
    component: (<div>{messages.SUBMISSION_ATTEMPTS_APPLY_SUCCESS}</div>),
    type: ELSWithToastService.types.POSITIVE,
  });
};

export const toastSubmissionAttemptsSkippedWarning = (toastService: ELSToastServiceType, messages: Messages) => {
  toastService.openToast({
    ...BASE_TOAST_CONFIG,
    component: (<div>{messages.SUBMISSION_ATTEMPTS_SKIPPED_WARNING}</div>),
    type: ELSWithToastService.types.PRIMARY,
  });
};

export const toastDueDateApplySuccess = (toastService: ELSToastServiceType) => {
  toastService.openToast({
    ...BASE_TOAST_CONFIG,
    component: (<div>Due date applied</div>),
    type: ELSWithToastService.types.POSITIVE,
  });
};

export const isGradeTypeUpdated = (originalAssignment: AssignmentDto, assignment: AssignmentDto) => {
  return originalAssignment
    ? originalAssignment.assignmentGradeType !== assignment.assignmentGradeType
    : assignment && assignment.assignmentGradeType && assignment.assignmentGradeType !== AssignmentGradeType.NOT_GRADED;
};

export const getPushDates = (availableDate: string, minutes: number, hours: number, days: number, weeks: number, selectedOptions: string) => {
  if (selectedOptions === extendDatesOptions[0].value) {
    return moment.utc(availableDate).add({ minutes, hours, days, weeks }).toISOString();
  }
  return moment.utc(availableDate).add({ minutes: -minutes, hours: -hours, days: -days, weeks: -weeks }).toISOString();
};

export const isInValidPushDateData = (pushDateData: number[] | string[]): boolean => {
  return pushDateData.some(item => ((item === '') || Number(item) < 0 || Number(item) > 999 || !isInteger(Number(item))));
};

export const checkAssignmentsAreEqual = (originalAssignment: AssignmentDto, updatedAssignment: AssignmentDto): boolean => {
  return updatedAssignment
    && originalAssignment.assignmentGradeType === updatedAssignment.assignmentGradeType
    && originalAssignment.title === updatedAssignment.title
    && originalAssignment.gradePoints === updatedAssignment.gradePoints
    && originalAssignment.maxRetries === updatedAssignment.maxRetries
    && areDatesTheSame(originalAssignment.availableDate, updatedAssignment.availableDate)
    && areDatesTheSame(originalAssignment.dueDate, updatedAssignment.dueDate);
};

export const hasAnyAssignmentBeenUpdated = (originalAssignmentsDictionary: Dictionary<AssignmentDto>, updatedAssignmentsDictionary: Dictionary<AssignmentDto>): boolean => {
  const originalAssignments = Object.values(originalAssignmentsDictionary);
  const updatedAssignments = Object.values(updatedAssignmentsDictionary);

  return updatedAssignments.some(updatedAssignment => {
    const originalAssignment = originalAssignments.find(original => {
      return original.id === updatedAssignment.id;
    });
    if (!originalAssignment) {
      return true;
    }
    return !checkAssignmentsAreEqual(originalAssignment, updatedAssignment);
  });
};

export const getAssignmentGradePointsDisplay = (assignment: AssignmentDto, config: { featureFlagsGrouped: FeatureFlagsGroupedDto[]; courseSectionId: string }): string => {
  const isEAQCustomGradePointsEnabled = getBooleanFromGroupFeatureFlagWithFallbackToGlobal(
    config.featureFlagsGrouped,
    FEATURE_FLAG.IS_EAQ_CUSTOM_GRADE_POINTS_ENABLED,
    config.courseSectionId
  );
  const isUnsupportedType = isEAQCustomGradePointsEnabled
    ? Object.values(UnSupportedGradePointsAssignmentTypeNoEAQ as unknown as AssignmentDto).includes(assignment?.assignmentType)
    : Object.values(UnSupportedGradePointsAssignmentType as unknown as AssignmentDto).includes(assignment?.assignmentType);

  if (!assignment || assignment.assignmentGradeType === AssignmentGradeType.NOT_GRADED || isUnsupportedType) {
    return 'No points';
  }
  return `${assignment.gradePoints} points`;
};

export const isGradePointsUpdated = (
  originalAssignment: AssignmentDto,
  assignment: AssignmentDto,
  config: { featureFlagsGrouped: FeatureFlagsGroupedDto[]; courseSectionId: string }
) => {
  return originalAssignment
    ? (originalAssignment.gradePoints !== assignment.gradePoints || originalAssignment.assignmentGradeType !== assignment.assignmentGradeType)
      && isAssignmentAllowGradePoints(assignment, config)
    : assignment && assignment.gradePoints && assignment.assignmentGradeType !== AssignmentGradeType.NOT_GRADED;
};
